/**
 * หน้าจอรับเข้าสต๊อก — รายการ / รายละเอียด / รับของเข้าใบใหม่
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับที่ `WO-P1-M10-001` วางไว้: อ่าน flag จาก `modules`
 * ของ permission set ที่เซิร์ฟเวอร์ส่งมาเท่านั้น ผ่าน `can()` **ห้าม derive กติกา
 * ADMIN/AUDITOR ซ้ำจาก `system_roles`** และทุกสถานะที่ไม่ใช่ `ready` แปลว่าไม่มีสิทธิ์
 *
 * `INVENTORY` + `CREATE` เป็นสิทธิ์ของ **การรับของเข้า** เท่านั้น ไม่ใช่ของการ
 * ปล่อยผ่านคุณภาพ ซึ่ง contract กำหนดให้ใช้ `INVENTORY` + `APPROVE` และเป็น
 * endpoint คนละตัวที่ยังไม่มีในบิลด์นี้ — การรวมสองอย่างเข้าด้วยกันจะแปลว่าทุกคน
 * ที่รับของเข้าได้ ก็ปล่อยของของตัวเองออกจากการกักได้ด้วย
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย การซ่อนปุ่ม "รับของเข้าสต๊อก"
 * ไม่ได้ป้องกันอะไรเลย — เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ ที่ซ่อนเพราะไม่
 * อยากให้ผู้ใช้กรอกฟอร์มแล้วโดนปฏิเสธตอนกดบันทึก
 */

import { useSearchParams } from 'react-router-dom';

import { GoodsReceiptCreateView } from './GoodsReceiptCreateView';
import { GoodsReceiptDetailView } from './GoodsReceiptDetailView';
import { GoodsReceiptListView } from './GoodsReceiptListView';
import {
  hasCrossPlantScope,
  receiptCreateSearch,
  receiptListSearch,
  resolveGoodsReceiptView,
  selectablePlants,
} from './goodsReceiptView';
import { StatusBadge } from '../StatusBadge';
import { INVENTORY_MODULE } from '../../lib/goodsReceipts/types';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';

export interface GoodsReceiptScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `fetch` ของเบราว์เซอร์ */
  readonly fetchImpl?: typeof fetch;
}

function CreateNotOfferedNotice() {
  return (
    <div className="cap-page">
      <div className="cap-notice" data-testid="goods-receipt-create-unavailable" role="status">
        <StatusBadge tone="warning">ไม่ได้เปิดฟอร์มให้</StatusBadge>
        <h1 className="cap-notice__title">สิทธิ์ชุดนี้ไม่มีการรับของเข้าสต๊อก</h1>
        <p className="cap-notice__text">
          สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_create</code>{' '}
          ของโมดูล <code className="cap-table__code">{INVENTORY_MODULE}</code>{' '}
          หน้าจอนี้จึงไม่เปิดฟอร์มให้กรอก
        </p>
        <p className="cap-notice__note">
          การไม่เปิดฟอร์มเป็นเพียง UX เพื่อไม่ให้กรอกงานที่จะถูกปฏิเสธตอนบันทึก —
          ผู้ตัดสินจริงคือ backend ทุกคำขอเสมอ
        </p>
      </div>
    </div>
  );
}

export function GoodsReceiptScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: GoodsReceiptScreenProps) {
  const [searchParams] = useSearchParams();

  const view = resolveGoodsReceiptView(searchParams);
  const canCreate = can(permissions, INVENTORY_MODULE, 'CREATE');

  // โรงงานมาจาก `plant_scope` ที่เซิร์ฟเวอร์ส่งมาเท่านั้น ไม่ใช่รายการที่หน้าจอคิดเอง
  const plants = selectablePlants(permissions);
  const crossPlant = hasCrossPlantScope(permissions);

  if (view.kind === 'create') {
    if (!canCreate) {
      return <CreateNotOfferedNotice />;
    }
    return (
      <GoodsReceiptCreateView
        apiBaseUrl={apiBaseUrl}
        fetchImpl={fetchImpl}
        plants={plants}
        listHref={receiptListSearch()}
      />
    );
  }

  if (view.kind === 'detail') {
    return (
      <GoodsReceiptDetailView
        receiptId={view.receiptId}
        apiBaseUrl={apiBaseUrl}
        fetchImpl={fetchImpl}
        listHref={receiptListSearch()}
      />
    );
  }

  return (
    <GoodsReceiptListView
      apiBaseUrl={apiBaseUrl}
      fetchImpl={fetchImpl}
      plants={plants}
      crossPlant={crossPlant}
      canCreate={canCreate}
      createHref={receiptCreateSearch()}
    />
  );
}
